// src/components/admin/OrgSettingsSectionProposalContact.tsx
//
// Org-wide default contact block for generated proposals.

import { useCallback, useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { OrgSettings } from "@/components/admin/orgSettingsSectionShared";
import { AddressAutocompleteInput } from "@/components/common/AddressAutocompleteInput";

type LoadedContact = {
  org_name: string;
  person_name: string;
  address: string;
  phone: string;
  email: string;
};

const BLANK: LoadedContact = {
  org_name: "",
  person_name: "",
  address: "",
  phone: "",
  email: "",
};

export function OrgSettingsSectionProposalContact(props: {
  sb: SupabaseClient;
  organizationId: string;
  branding: Pick<OrgSettings, "brand_name" | "organization_name">;
}) {
  const { sb, organizationId, branding } = props;
  const [original, setOriginal] = useState<LoadedContact>(BLANK);
  const [draft, setDraft] = useState<LoadedContact>(BLANK);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await sb
        .from("organizations")
        .select(
          "name, proposal_contact_org_name, proposal_contact_person_name, proposal_contact_address, proposal_contact_phone, proposal_contact_email, brand_name",
        )
        .eq("id", organizationId)
        .maybeSingle();
      if (error) throw error;
      const row = (data ?? {}) as Record<string, string | null>;
      const orgName = row.name?.trim() || branding.organization_name.trim();
      const brand = row.brand_name?.trim() || branding.brand_name.trim() || orgName;
      const loaded: LoadedContact = {
        org_name: row.proposal_contact_org_name?.trim() || brand || orgName,
        person_name: row.proposal_contact_person_name?.trim() || "",
        address: row.proposal_contact_address?.trim() || "",
        phone: row.proposal_contact_phone?.trim() || "",
        email: row.proposal_contact_email?.trim() || "",
      };
      setOriginal(loaded);
      setDraft(loaded);
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : String(e);
      if (!/does not exist|schema cache|Could not find/i.test(errMsg)) {
        setMsg({ text: errMsg, ok: false });
      }
    } finally {
      setLoading(false);
    }
  }, [branding.brand_name, branding.organization_name, organizationId, sb]);

  useEffect(() => {
    void load();
  }, [load]);

  const isDirty =
    draft.org_name !== original.org_name ||
    draft.person_name !== original.person_name ||
    draft.address !== original.address ||
    draft.phone !== original.phone ||
    draft.email !== original.email;

  async function save() {
    setMsg(null);
    if (!draft.org_name.trim()) {
      setMsg({ text: "Organization name is required.", ok: false });
      return;
    }
    setSaving(true);
    try {
      const { error } = await sb.rpc("opp_update_org_proposal_contact", {
        p_org_id: organizationId,
        p_org_name: draft.org_name.trim(),
        p_person_name: draft.person_name.trim() || null,
        p_address: draft.address.trim() || null,
        p_phone: draft.phone.trim() || null,
        p_email: draft.email.trim() || null,
      });
      if (error) throw error;
      setOriginal({ ...draft });
      setMsg({ text: "Proposal contact defaults saved.", ok: true });
      window.dispatchEvent(new CustomEvent("org-settings-saved"));
    } catch (e) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="pt-2 border-t border-gray-100">
      <div className="mb-2">
        <h3 className="text-sm font-semibold text-gray-700">Proposal contact defaults</h3>
        <p className="text-xs text-gray-400 mt-0.5">
          Shown on generated proposals unless a manager format override or sales-rep override
          applies. Organization name defaults to your brand name, or the workspace name when
          brand name is unset.
        </p>
      </div>

      {msg ? (
        <div
          className={`text-xs rounded-lg px-3 py-2 border mb-2 ${
            msg.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
        >
          {msg.text}
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="space-y-1 sm:col-span-2">
          <span className="text-xs font-medium text-gray-600">Organization name *</span>
          <input
            type="text"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            value={draft.org_name}
            onChange={(e) => setDraft((d) => ({ ...d, org_name: e.target.value }))}
            disabled={loading || saving}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-gray-600">Contact name</span>
          <input
            type="text"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            value={draft.person_name}
            onChange={(e) => setDraft((d) => ({ ...d, person_name: e.target.value }))}
            disabled={loading || saving}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-gray-600">Phone</span>
          <input
            type="text"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            value={draft.phone}
            onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
            disabled={loading || saving}
          />
        </label>
        <label className="space-y-1 sm:col-span-2">
          <span className="text-xs font-medium text-gray-600">Email</span>
          <input
            type="email"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            value={draft.email}
            onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
            disabled={loading || saving}
          />
        </label>
        <label className="space-y-1 sm:col-span-2">
          <span className="text-xs font-medium text-gray-600">Address</span>
          <AddressAutocompleteInput
            value={draft.address}
            onChange={(next) => setDraft((d) => ({ ...d, address: next }))}
            disabled={loading || saving}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          />
        </label>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={loading || saving || !isDirty}
          className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save contact defaults"}
        </button>
        {isDirty ? (
          <button
            type="button"
            onClick={() => {
              setDraft(original);
              setMsg(null);
            }}
            className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
          >
            Discard
          </button>
        ) : null}
      </div>
    </div>
  );
}
